/**
 * Country flags from the shared SVG sprite.
 *
 * The imports register one `<symbol>` per file into the sprite that
 * `svg-sprite-loader` assembles at build time. The symbol ids carry no `leap-`
 * prefix — `webpack.config.js` derives the prefix from the SVG's own path, and
 * these files live outside `app/leap/` — so the id is the bare file name (`DE`,
 * `GB`, …), which is what the map below is keyed by.
 *
 * The map holds each flag's `viewBox` because the imported module does not: the
 * loader leaves it undefined here, which is why `./index.jsx` carries `||`
 * fallbacks for its own glyphs. Without one the browser falls back to the
 * default 300x150 SVG viewport, so a caller sizing one axis gets a 150px-tall
 * flag. Holding the real value lets each artwork keep its own proportions —
 * they differ, most flags being 3:2 and Ukraine 4:3, so a single shared ratio
 * would letterbox the rest. `flags.test.js` reads the SVGs off disk and holds
 * these values against them, so a replaced artwork cannot drift.
 *
 * Deliberately NOT re-exported from `./index.jsx`, so reaching for one ordinary
 * glyph does not drag all 75 flags in. That only spares *other* icon consumers
 * though — this module imports the whole set unconditionally, and the header
 * pulls it in on every page. Trimming what ships (a per-locale subset, a
 * dynamic import) is a change to these imports, not to where the barrel sits.
 */
import '../../../assets/svg/flags/AE.svg'
import '../../../assets/svg/flags/AL.svg'
import '../../../assets/svg/flags/AM.svg'
import '../../../assets/svg/flags/AR.svg'
import '../../../assets/svg/flags/AT.svg'
import '../../../assets/svg/flags/AU.svg'
import '../../../assets/svg/flags/AZ.svg'
import '../../../assets/svg/flags/BA.svg'
import '../../../assets/svg/flags/BE.svg'
import '../../../assets/svg/flags/BG.svg'
import '../../../assets/svg/flags/BN.svg'
import '../../../assets/svg/flags/BR.svg'
import '../../../assets/svg/flags/BY.svg'
import '../../../assets/svg/flags/CA.svg'
import '../../../assets/svg/flags/CH.svg'
import '../../../assets/svg/flags/CL.svg'
import '../../../assets/svg/flags/CN.svg'
import '../../../assets/svg/flags/CO.svg'
import '../../../assets/svg/flags/CR.svg'
import '../../../assets/svg/flags/CY.svg'
import '../../../assets/svg/flags/CZ.svg'
import '../../../assets/svg/flags/DE.svg'
import '../../../assets/svg/flags/DK.svg'
import '../../../assets/svg/flags/EE.svg'
import '../../../assets/svg/flags/EG.svg'
import '../../../assets/svg/flags/ES.svg'
import '../../../assets/svg/flags/EU.svg'
import '../../../assets/svg/flags/FI.svg'
import '../../../assets/svg/flags/FR.svg'
import '../../../assets/svg/flags/GB.svg'
import '../../../assets/svg/flags/GE.svg'
import '../../../assets/svg/flags/GR.svg'
import '../../../assets/svg/flags/HK.svg'
import '../../../assets/svg/flags/HR.svg'
import '../../../assets/svg/flags/HU.svg'
import '../../../assets/svg/flags/ID.svg'
import '../../../assets/svg/flags/IE.svg'
import '../../../assets/svg/flags/IL.svg'
import '../../../assets/svg/flags/IN.svg'
import '../../../assets/svg/flags/IR.svg'
import '../../../assets/svg/flags/IS.svg'
import '../../../assets/svg/flags/IT.svg'
import '../../../assets/svg/flags/JP.svg'
import '../../../assets/svg/flags/KG.svg'
import '../../../assets/svg/flags/KR.svg'
import '../../../assets/svg/flags/KZ.svg'
import '../../../assets/svg/flags/LT.svg'
import '../../../assets/svg/flags/LU.svg'
import '../../../assets/svg/flags/LV.svg'
import '../../../assets/svg/flags/MX.svg'
import '../../../assets/svg/flags/MY.svg'
import '../../../assets/svg/flags/NL.svg'
import '../../../assets/svg/flags/NO.svg'
import '../../../assets/svg/flags/NZ.svg'
import '../../../assets/svg/flags/PA.svg'
import '../../../assets/svg/flags/PE.svg'
import '../../../assets/svg/flags/PH.svg'
import '../../../assets/svg/flags/PL.svg'
import '../../../assets/svg/flags/PR.svg'
import '../../../assets/svg/flags/PT.svg'
import '../../../assets/svg/flags/QA.svg'
import '../../../assets/svg/flags/RO.svg'
import '../../../assets/svg/flags/RS.svg'
import '../../../assets/svg/flags/RU.svg'
import '../../../assets/svg/flags/SE.svg'
import '../../../assets/svg/flags/SG.svg'
import '../../../assets/svg/flags/SI.svg'
import '../../../assets/svg/flags/SK.svg'
import '../../../assets/svg/flags/TH.svg'
import '../../../assets/svg/flags/TR.svg'
import '../../../assets/svg/flags/UA.svg'
import '../../../assets/svg/flags/UK.svg'
import '../../../assets/svg/flags/US.svg'
import '../../../assets/svg/flags/XB.svg'
import '../../../assets/svg/flags/ZA.svg'

const FLAG_VIEW_BOXES = {
    AE: '0 0 513 343',
    AL: '0 0 513 343',
    AM: '0 0 513 343',
    AR: '0 0 513 343',
    AT: '0 0 513 343',
    AU: '0 0 513 343',
    AZ: '0 0 513 343',
    BA: '0 0 513 343',
    BE: '0 0 513 343',
    BG: '0 0 513 343',
    BN: '0 0 513 343',
    BR: '0 0 513 343',
    BY: '0 0 513 343',
    CA: '0 0 513 343',
    CH: '0 0 513 343',
    CL: '0 0 513 343',
    CN: '0 0 513 343',
    CO: '0 0 513 343',
    CR: '0 0 513 343',
    CY: '0 0 513 343',
    CZ: '0 0 513 343',
    DE: '0 0 513 343',
    DK: '0 0 513 343',
    EE: '0 0 513 343',
    EG: '0 0 513 343',
    ES: '0 0 513 343',
    EU: '0 0 513 343',
    FI: '0 0 513 343',
    FR: '0 0 513 343',
    GB: '0 0 513 343',
    GE: '0 0 513 343',
    GR: '0 0 513 343',
    HK: '0 0 513 343',
    HR: '0 0 513 343',
    HU: '0 0 513 343',
    ID: '0 0 513 343',
    IE: '0 0 513 343',
    IL: '0 0 513 343',
    IN: '0 0 513 343',
    IR: '0 0 513 343',
    IS: '0 0 513 343',
    IT: '0 0 513 343',
    JP: '0 0 513 343',
    KG: '0 0 513 344',
    KR: '0 0 513 343',
    KZ: '0 0 513 343',
    LT: '0 0 513 343',
    LU: '0 0 513 343',
    LV: '0 0 513 343',
    MX: '0 0 513 343',
    MY: '0 0 513 343',
    NL: '0 0 513 343',
    NO: '0 0 513 343',
    NZ: '0 0 513 343',
    PA: '0 0 513 343',
    PE: '0 0 513 343',
    PH: '0 0 513 343',
    PL: '0 0 513 343',
    PR: '0 0 513 343',
    PT: '0 0 513 343',
    QA: '0 0 513 343',
    RO: '0 0 513 343',
    RS: '0 0 513 343',
    RU: '0 0 513 343',
    SE: '0 0 513 343',
    SG: '0 0 513 343',
    SI: '0 0 513 343',
    SK: '0 0 513 343',
    TH: '0 0 513 343',
    TR: '0 0 513 343',
    UA: '0 0 640 480',
    UK: '0 0 513 343',
    US: '0 0 513 343',
    XB: '0 0 513 342',
    ZA: '0 0 513 343'
}

/**
 * Country codes the sprite can render, derived from the map so the two cannot
 * disagree. A caller uses this to tell "no flag for this country" apart from
 * "flag failed to render" — a `<use>` pointing at a missing symbol paints
 * nothing and leaves a hole in the layout.
 */
export const FLAG_COUNTRY_CODES = Object.keys(FLAG_VIEW_BOXES)

/**
 * The `viewBox` for a country's flag, or undefined when there is no flag.
 *
 * @param {string} code - Uppercase ISO 3166-1 alpha-2 code.
 * @returns {string|undefined}
 */
export const flagViewBox = (code) => FLAG_VIEW_BOXES[code]
